import { Component, input, OnChanges, OnDestroy, output } from '@angular/core';
import { uuid } from '@core/helpers';
import { TAllUsers } from '@project/view-models';
import { Subject, takeUntil } from 'rxjs';
import { sanitizeHTML } from 'src/app/core/helpers/text-sanitizer';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { TUser } from 'src/app/project/modules/user/user.model';
import { UserService } from 'src/app/project/modules/user/user.service';
import { UsersDropdownComponent } from 'src/app/project/modules/users/users-dropdown/users-dropdown.component';
import { TUsersDropdownData } from 'src/app/project/modules/users/users-dropdown/users-dropdown.model';
import { UsersService } from 'src/app/project/modules/users/users.service';
import { EAccessField } from 'src/app/project/shared/enums/access-field.enum';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { PointsUsersService } from '../../../points-users.service';

@Component({
  selector: 'pp-point-reminder-modal-user-picker',
  templateUrl: './point-reminder-modal-user-picker.component.html',
  styleUrl: './point-reminder-modal-user-picker.component.scss',
  standalone: false,
})
export class PointReminderModalUserPickerComponent implements OnChanges, OnDestroy {
  ppAssignees = input<string[]>();
  ppUsersChange = output<string[]>();
  ppEditable = input<boolean>();
  ppPointId = input<string>();
  ppTargetUserId = input<string>();
  ppGuestUser = input<boolean>();

  EIconPath = EIconPath;
  dropdown: TDropdown;
  buttonId = uuid();
  allUsers: TAllUsers;
  tooltipText = '';
  assignees: string[] = [];
  private readonly destroy$ = new Subject<void>();
  private processing = false;
  private pointUserIds: string[] = [];
  private user: TUser;

  constructor(
    private dropdownService: DropdownService,
    private usersService: UsersService,
    private pointsUsersService: PointsUsersService,
    private userService: UserService,
  ) {
    this.dropdown = this.dropdownService.getDropdown();
    this.allUsers = this.usersService.getUsers();
    this.user = this.userService.getUser();
  }

  ngOnChanges(): void {
    this.assignees = this.ppAssignees().filter((userId) => userId !== this.ppTargetUserId());
    this.generateTooltipContent();

    this.fetchUsersForPoint();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private fetchUsersForPoint(): void {
    if (!this.ppPointId()) {
      this.pointUserIds = [];
      this.processing = false;
      return;
    }

    this.processing = true;
    this.pointsUsersService
      .fetchUsersWithAccessToPoint(this.ppPointId(), EAccessField.DESCRIPTION)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userIds) => {
        this.pointUserIds = userIds;
        this.processing = false;
      });
  }

  showUsersDropdown(): void {
    if (!this.ppPointId() || this.processing || this.ppGuestUser()) {
      return;
    }

    if (this.dropdown.visible && this.dropdown.buttonId === this.buttonId) {
      this.dropdownService.hideDropdown();
    } else {
      this.dropdownService.setData<TUsersDropdownData>({
        unselectedUsers: Object.values(this.allUsers)
          .filter((user) => user.verified)
          .map((user) => user.userId)
          .filter(
            (userId) =>
              !this.ppAssignees().includes(userId) &&
              this.pointUserIds.includes(userId) &&
              userId !== this.user.userId,
          ),
        allUsers: Object.keys(this.allUsers).filter(
          (userId) => this.pointUserIds.includes(userId) && userId !== this.user.userId,
        ),
        type: 'assignees',
        showButtons: true,
        disabled: false,
        width: 326,
      });

      this.dropdownService.showDropdown(this.buttonId, UsersDropdownComponent, {
        callback: (id) => this.selectUser(id),
        suppressScrollbar: true,
      });
    }
  }

  selectUser(userId: string): void {
    if (!userId) {
      this.ppUsersChange.emit([]);
      return;
    }

    const assignees = this.ppAssignees();

    if (assignees.includes(userId)) {
      this.ppUsersChange.emit(assignees.filter((id) => id !== userId));
    } else {
      this.ppUsersChange.emit([...assignees, userId]);
    }
  }

  private generateTooltipContent(): void {
    if (this.assignees.length < 3) {
      this.tooltipText = '';
      return;
    }

    const assigneesToShow = this.assignees.slice(3);

    let tooltipText = '<div class="tooltipTable">';

    assigneesToShow.forEach((userId) => {
      tooltipText +=
        `            <div class="tooltipTableRow">
              <div class="tooltipAvatar__wrapper">
                <img class="labelTooltipAvatar" src="${this.allUsers[userId].avatarPublicUrl}" alt="${EIconPath.PLACEHOLDER_USER}"/>
              </div>
              <span class="tooltipText--short tooltipTableCell">
                <span class="tooltipInnerCell" >
                  <span class="labelTooltip__userName">` +
        sanitizeHTML(this.allUsers[userId].userName) +
        `</span>
                  <span class="labelTooltip__email"> ` +
        sanitizeHTML(this.allUsers[userId].email) +
        `</span>
                </span>
              </span>
            </div>
`;
    });

    tooltipText += '</div>';

    this.tooltipText = tooltipText;
  }
}
